import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { ChemicalTemplateService, CreateChemicalTemplateDto, UpdateChemicalTemplateDto } from '../../../../../core/services/chemical-template.service';
import { ChemicalService } from '../../../../../core/services/chemical.service';
import { CustomChemicalService } from '../../../../../core/services/custom-chemical.service';
import { RATE_UNIT_OPTIONS, buildRateString, parseRateToStructured, parseRateUnit } from 'src/app/core/models';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';

interface CropOption {
  value: string;
  label: string;
}

interface LookupChemical {
  id: string;
  name: string;
  displayName?: string;
  epaNumber: string;
  manufacturer: string;
  activeIngredient: string;
  category: string;
  source: 'epa' | 'adn-recommended' | 'custom';
  defaultRate?: string;
  signalWord?: string;
  formulation?: string;
}

@Component({
  selector: 'app-chemical-template-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './chemical-template-form.component.html',
  styleUrls: ['./chemical-template-form.component.scss']
})
export class ChemicalTemplateFormComponent implements OnInit, OnDestroy {
  form!: FormGroup;
  isEditMode = false;
  templateId: string | null = null;
  isLoading = false;
  isSaving = false;

  private chemicalTemplateService = inject(ChemicalTemplateService);
  private chemicalService = inject(ChemicalService);
  private customChemicalService = inject(CustomChemicalService);

  // Application timing options
  applicationTimings = [
    { value: 'pre-emergence', label: 'Pre-emergence' },
    { value: 'post-emergence', label: 'Post-emergence' },
    { value: 'early-season', label: 'Early season' },
    { value: 'mid-season', label: 'Mid-season' },
    { value: 'late-season', label: 'Late season' },
    { value: 'as-needed', label: 'As needed based on scouting' },
    { value: 'preventive', label: 'Preventive' },
    { value: 'curative', label: 'Curative' }
  ];

  // Rate unit options for structured rate input
  rateUnitOptions = RATE_UNIT_OPTIONS;

  // Common volume units
  volumeUnits = ['gal/acre', 'qt/acre', 'pt/acre', 'oz/acre', 'L/ha'];

  // Common crop options
  cropOptions: CropOption[] = [
    { value: 'Corn', label: 'Corn' },
    { value: 'Soybeans', label: 'Soybeans' },
    { value: 'Wheat', label: 'Wheat' },
    { value: 'Cotton', label: 'Cotton' },
    { value: 'Alfalfa', label: 'Alfalfa' },
    { value: 'Canola', label: 'Canola' },
    { value: 'Sunflowers', label: 'Sunflowers' },
    { value: 'Rice', label: 'Rice' },
    { value: 'Sorghum', label: 'Sorghum' },
    { value: 'Barley', label: 'Barley' },
    { value: 'Oats', label: 'Oats' },
    { value: 'Apples', label: 'Apples' },
    { value: 'Pears', label: 'Pears' },
    { value: 'Citrus', label: 'Citrus' },
    { value: 'Grapes', label: 'Grapes' },
    { value: 'Vegetables', label: 'Vegetables' }
  ];

  // Common pest options
  pestOptions = [
    'Broadleaf weeds',
    'Annual grasses',
    'Perennial grasses',
    'Waterhemp',
    'Palmer amaranth',
    'Giant ragweed',
    'Marestail',
    'Aphids',
    'Armyworms',
    'Corn borers',
    'Stink bugs',
    'Thrips',
    'Mites',
    'Apple scab',
    'Powdery mildew',
    'Rust',
    'Fusarium',
    'Leaf spot'
  ];

  // Selected items
  selectedCrops: string[] = [];
  selectedPests: string[] = [];

  // For adding new items not in the list
  newCropInput = '';
  newPestInput = '';

  // Chemical lookup
  showChemicalLookup = false;
  chemicalSearchQuery = '';
  chemicalSourceFilter: 'epa' | 'adn-recommended' | 'custom' = 'adn-recommended';
  customChemicalsLoaded: LookupChemical[] = [];
  adnChemicalsLoaded: LookupChemical[] = [];
  filteredLookupChemicals: LookupChemical[] = [];
  activeChemicalIndex: number | null = null;
  isSearchingLookup = false;
  private searchTimeout: ReturnType<typeof setTimeout> | null = null;
  /** Monotonic counter to discard stale EPA search responses */
  private searchRequestId = 0;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadLookupChemicals();
    this.checkEditMode();
  }

  ngOnDestroy(): void {
    this.searchRequestId++; // Discard any in-flight EPA responses after teardown
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  }

  private async loadLookupChemicals(): Promise<void> {
    try {
      // Load ADN recommended chemicals (preload all — manageable set)
      // Use searchChemicalsQuiet to avoid overwriting Chemical Book's shared signal state
      const adnResult = await this.chemicalService.searchChemicalsQuiet('', { source: 'adn-recommended', limit: 200 });
      this.adnChemicalsLoaded = adnResult.chemicals.map(c => ({
        id: c.id,
        name: c.name,
        displayName: c.displayName || c.name,
        epaNumber: c.epaNumber || 'N/A',
        manufacturer: c.manufacturer || 'Unknown',
        activeIngredient: c.activeIngredient || 'Unknown',
        category: c.category,
        source: 'adn-recommended' as const,
        defaultRate: c.defaultRate || '',
        signalWord: c.signalWord || '',
        formulation: c.formulation || ''
      }));

      // Load custom chemicals (preload all — small per-org set)
      await this.customChemicalService.loadChemicals();
      this.customChemicalsLoaded = this.customChemicalService.chemicals().map((c: any) => ({
        id: c.id,
        name: c.name,
        epaNumber: c.epaNumber || 'Custom',
        manufacturer: c.manufacturer || 'Your Organization',
        activeIngredient: c.activeIngredient || 'Custom blend',
        category: c.category,
        source: 'custom' as const,
        defaultRate: c.defaultRate || '',
        signalWord: c.signalWord || '',
        formulation: c.formulation || ''
      }));

      // Default tab: My Chemicals if user has any, otherwise ADN Recommended
      this.chemicalSourceFilter = this.customChemicalsLoaded.length > 0 ? 'custom' : 'adn-recommended';
    } catch (error) {
      console.error('Error loading lookup chemicals:', error);
      this.customChemicalsLoaded = [];
      this.adnChemicalsLoaded = [];
      this.filteredLookupChemicals = [];
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Basic Information
      name: ['', [Validators.required, Validators.minLength(3)]],
      description: [''],

      // Chemicals in this template (backend calls it "items" but form uses "chemicals" for clarity)
      chemicals: this.fb.array([], Validators.required),

      // Application details
      applicationTiming: [''],
      totalVolume: ['', Validators.required],
      volumeUnit: ['gal/acre'],

      // Cost tracking
      estimatedCostPerAcre: [null],

      // Notes
      notes: ['']
    });
  }

  // Chemicals FormArray management
  get chemicals(): FormArray {
    return this.form.get('chemicals') as FormArray;
  }

  addChemical(): void {
    const chemicalGroup = this.fb.group({
      name: ['', Validators.required],
      rateValue: [null as number | null, [Validators.required, Validators.min(0.01)]],
      rateUnit: [null as string | null, Validators.required],
      purpose: [''],
      source: ['manual'],
      chemicalId: ['']
    });
    this.chemicals.push(chemicalGroup);
  }

  // Chemical lookup methods
  openChemicalLookup(index: number): void {
    this.activeChemicalIndex = index;
    this.showChemicalLookup = true;
    this.chemicalSearchQuery = '';
    this.chemicalSourceFilter = this.customChemicalsLoaded.length > 0 ? 'custom' : 'adn-recommended';
    this.filterLookupChemicals();
  }

  closeChemicalLookup(): void {
    this.showChemicalLookup = false;
    this.activeChemicalIndex = null;
    this.chemicalSearchQuery = '';
    this.searchRequestId++; // Invalidate any in-flight EPA requests
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = null;
    }
    this.isSearchingLookup = false;
    this.filteredLookupChemicals = [];
  }

  filterLookupChemicals(): void {
    if (this.chemicalSourceFilter === 'epa') {
      // EPA: server-side search (debounced)
      this.searchEpaChemicals();
      return;
    }

    // Switching away from EPA — invalidate any in-flight EPA requests
    this.searchRequestId++;
    this.isSearchingLookup = false;

    // Custom and ADN: client-side filter from preloaded data
    const source = this.chemicalSourceFilter === 'custom'
      ? this.customChemicalsLoaded
      : this.adnChemicalsLoaded;

    if (this.chemicalSearchQuery.trim()) {
      const query = this.chemicalSearchQuery.toLowerCase();
      this.filteredLookupChemicals = source.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.activeIngredient.toLowerCase().includes(query) ||
        c.manufacturer.toLowerCase().includes(query) ||
        c.epaNumber.toLowerCase().includes(query) ||
        c.category.toLowerCase().includes(query)
      );
    } else {
      this.filteredLookupChemicals = [...source];
    }
  }

  private searchEpaChemicals(): void {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    const query = this.chemicalSearchQuery.trim();
    if (!query) {
      this.searchRequestId++; // Invalidate any in-flight EPA requests
      this.filteredLookupChemicals = [];
      this.isSearchingLookup = false;
      return;
    }

    this.isSearchingLookup = true;
    this.filteredLookupChemicals = []; // Clear stale results so they don't flicker under the spinner
    const requestId = ++this.searchRequestId;
    this.searchTimeout = setTimeout(async () => {
      this.searchTimeout = null; // Timer has fired; clear ref so lifecycle cleanup stays accurate
      try {
        // Use searchChemicalsQuiet to avoid overwriting Chemical Book's shared signal state
        const result = await this.chemicalService.searchChemicalsQuiet(query, { source: 'epa', limit: 50 });
        // Discard if a newer search was initiated while this one was in flight
        if (requestId !== this.searchRequestId) return;
        this.filteredLookupChemicals = result.chemicals.map(c => ({
          id: c.id,
          name: c.name,
          displayName: c.displayName || c.name,
          epaNumber: c.epaNumber || 'N/A',
          manufacturer: c.manufacturer || 'Unknown',
          activeIngredient: c.activeIngredient || 'Unknown',
          category: c.category,
          source: 'epa' as const,
          defaultRate: c.defaultRate || '',
          signalWord: c.signalWord || '',
          formulation: c.formulation || ''
        }));
      } catch (error) {
        if (requestId !== this.searchRequestId) return;
        console.error('Error searching EPA chemicals:', error);
        this.filteredLookupChemicals = [];
      } finally {
        if (requestId === this.searchRequestId) {
          this.isSearchingLookup = false;
        }
      }
    }, 300);
  }

  selectLookupChemical(chemical: LookupChemical): void {
    if (this.activeChemicalIndex !== null) {
      const chemicalControl = this.chemicals.at(this.activeChemicalIndex) as FormGroup;
      const parsedRate = chemical.defaultRate ? parseRateToStructured(chemical.defaultRate) : null;
      chemicalControl.patchValue({
        name: chemical.name,
        rateValue: parsedRate?.value ?? null,
        rateUnit: parsedRate?.rateUnit ?? null,
        source: chemical.source,
        chemicalId: chemical.id
      });
    }
    this.closeChemicalLookup();
  }

  addChemicalFromLookup(chemical: LookupChemical): void {
    const parsedRate = chemical.defaultRate ? parseRateToStructured(chemical.defaultRate) : null;
    // Check if first chemical is empty (placeholder) and replace it instead of adding
    if (this.chemicals.length === 1) {
      const firstChemical = this.chemicals.at(0) as FormGroup;
      const firstName = firstChemical.get('name')?.value?.trim();
      const firstRate = firstChemical.get('rateValue')?.value;

      // If name and rate are both empty, replace the placeholder
      if (!firstName && !firstRate) {
        firstChemical.patchValue({
          name: chemical.name,
          rateValue: parsedRate?.value ?? null,
          rateUnit: parsedRate?.rateUnit ?? null,
          source: chemical.source,
          chemicalId: chemical.id
        });
        this.closeChemicalLookup();
        return;
      }
    }

    // Otherwise, add as new chemical
    const chemicalGroup = this.fb.group({
      name: [chemical.name, Validators.required],
      rateValue: [parsedRate?.value ?? null, [Validators.required, Validators.min(0.01)]],
      rateUnit: [parsedRate?.rateUnit ?? null, Validators.required],
      purpose: [''],
      source: [chemical.source],
      chemicalId: [chemical.id]
    });
    this.chemicals.push(chemicalGroup);
    this.closeChemicalLookup();
  }

  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'epa': return 'badge-epa';
      case 'adn-recommended': return 'badge-adn';
      case 'custom': return 'badge-custom';
      default: return 'bg-secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'epa': return 'EPA';
      case 'adn-recommended': return 'AA';
      case 'custom': return 'Custom';
      default: return source;
    }
  }

  getCategoryBadgeClass(category: string): string {
    switch (category) {
      case 'herbicide': return 'badge-herbicide';
      case 'insecticide': return 'badge-insecticide';
      case 'fungicide': return 'badge-fungicide';
      case 'fertilizer': return 'badge-fertilizer';
      case 'adjuvant': return 'badge-adjuvant';
      case 'foliar': return 'badge-foliar';
      case 'igr': return 'badge-igr';
      case 'miticide': return 'badge-miticide';
      case 'pgr': return 'badge-pgr';
      default: return 'bg-secondary';
    }
  }

  getCategoryLabel(category: string): string {
    switch (category) {
      case 'herbicide': return 'Herbicide';
      case 'insecticide': return 'Insecticide';
      case 'fungicide': return 'Fungicide';
      case 'fertilizer': return 'Fertilizer';
      case 'adjuvant': return 'Adjuvant';
      case 'igr': return 'IGR';
      case 'miticide': return 'Miticide';
      case 'pgr': return 'PGR';
      case 'other': return 'Other';
      default: return category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Other';
    }
  }

  getSignalWordClass(signalWord: string): string {
    switch (signalWord) {
      case 'CAUTION': return 'signal-caution';
      case 'WARNING': return 'signal-warning';
      case 'DANGER': return 'signal-danger';
      default: return '';
    }
  }

  removeChemical(index: number): void {
    this.chemicals.removeAt(index);
  }

  moveChemicalUp(index: number): void {
    if (index > 0) {
      const item = this.chemicals.at(index);
      this.chemicals.removeAt(index);
      this.chemicals.insert(index - 1, item);
    }
  }

  moveChemicalDown(index: number): void {
    if (index < this.chemicals.length - 1) {
      const item = this.chemicals.at(index);
      this.chemicals.removeAt(index);
      this.chemicals.insert(index + 1, item);
    }
  }

  // Crop selection management
  toggleCrop(crop: string): void {
    const index = this.selectedCrops.indexOf(crop);
    if (index > -1) {
      this.selectedCrops.splice(index, 1);
    } else {
      this.selectedCrops.push(crop);
    }
  }

  isCropSelected(crop: string): boolean {
    return this.selectedCrops.includes(crop);
  }

  selectAllCrops(): void {
    this.selectedCrops = this.cropOptions.map(c => c.value);
  }

  clearCrops(): void {
    this.selectedCrops = [];
  }

  addCustomCrop(): void {
    if (this.newCropInput.trim() && !this.selectedCrops.includes(this.newCropInput.trim())) {
      this.selectedCrops.push(this.newCropInput.trim());
      if (!this.cropOptions.some(c => c.value === this.newCropInput.trim())) {
        this.cropOptions.push({ value: this.newCropInput.trim(), label: this.newCropInput.trim() });
      }
      this.newCropInput = '';
    }
  }

  removeCrop(crop: string): void {
    const index = this.selectedCrops.indexOf(crop);
    if (index > -1) {
      this.selectedCrops.splice(index, 1);
    }
  }

  // Pest selection management
  togglePest(pest: string): void {
    const index = this.selectedPests.indexOf(pest);
    if (index > -1) {
      this.selectedPests.splice(index, 1);
    } else {
      this.selectedPests.push(pest);
    }
  }

  isPestSelected(pest: string): boolean {
    return this.selectedPests.includes(pest);
  }

  clearPests(): void {
    this.selectedPests = [];
  }

  addCustomPest(): void {
    if (this.newPestInput.trim() && !this.selectedPests.includes(this.newPestInput.trim())) {
      this.selectedPests.push(this.newPestInput.trim());
      if (!this.pestOptions.includes(this.newPestInput.trim())) {
        this.pestOptions.push(this.newPestInput.trim());
      }
      this.newPestInput = '';
    }
  }

  removePest(pest: string): void {
    const index = this.selectedPests.indexOf(pest);
    if (index > -1) {
      this.selectedPests.splice(index, 1);
    }
  }

  private checkEditMode(): void {
    this.templateId = this.route.snapshot.paramMap.get('id');
    if (this.templateId) {
      this.isEditMode = true;
      this.loadTemplate(this.templateId);
    } else {
      // Add one empty chemical row by default for new templates
      this.addChemical();
    }
  }

  private async loadTemplate(id: string): Promise<void> {
    this.isLoading = true;
    console.log('Loading template:', id);

    // Clear existing data
    this.clearChemicals();
    this.selectedCrops = [];
    this.selectedPests = [];

    try {
      const template = await this.chemicalTemplateService.getTemplateById(id);
      if (template) {
        // Parse totalVolume back to number and unit
        const volumeParts = (template.totalVolume || '').split(' ');
        const volumeNumber = volumeParts[0] || '';
        const volumeUnit = volumeParts.slice(1).join(' ') || 'gal/acre';

        this.form.patchValue({
          name: template.name,
          description: template.description || '',
          applicationTiming: template.applicationTiming || '',
          totalVolume: volumeNumber,
          volumeUnit: volumeUnit,
          estimatedCostPerAcre: template.estimatedCostPerAcre || null,
          notes: template.notes || ''
        });

        // Load chemicals from template.items
        if (template.items) {
          template.items.forEach(item => {
            const parsedRate = item.rate ? parseRateToStructured(item.rate) : null;
            const rateValue = item.rateValue != null ? Number(item.rateValue) : parsedRate?.value ?? null;
            const rateUnit = (item.rateUnit && item.ratePerUnit)
              ? `${item.rateUnit}/${item.ratePerUnit}`
              : parsedRate?.rateUnit ?? null;

            const chemicalGroup = this.fb.group({
              name: [item.name, Validators.required],
              rateValue: [rateValue, [Validators.required, Validators.min(0.01)]],
              rateUnit: [rateUnit, Validators.required],
              purpose: [item.purpose || ''],
              source: ['manual'], // Will be determined from chemicalId/customChemicalId
              chemicalId: [item.chemicalId || item.customChemicalId || '']
            });
            this.chemicals.push(chemicalGroup);
          });
        }

        // Load selected crops and pests
        if (template.targetCrops) {
          this.selectedCrops = [...template.targetCrops];
        }
        if (template.targetPests) {
          this.selectedPests = [...template.targetPests];
        }
      } else {
        console.error('Template not found');
      }
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      this.isLoading = false;
    }
  }

  private clearChemicals(): void {
    while (this.chemicals.length > 0) {
      this.chemicals.removeAt(0);
    }
  }

  getFullVolume(): string {
    const volume = this.form.get('totalVolume')?.value;
    const unit = this.form.get('volumeUnit')?.value;
    if (volume && unit) {
      return `${volume} ${unit}`;
    }
    return '';
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.get(key)?.markAsTouched();
      });
      // Mark chemicals as touched
      this.chemicals.controls.forEach(control => {
        Object.keys((control as FormGroup).controls).forEach(key => {
          (control as FormGroup).get(key)?.markAsTouched();
        });
      });
      return;
    }

    if (this.selectedCrops.length === 0) {
      return;
    }

    if (this.chemicals.length === 0) {
      return;
    }

    this.isSaving = true;

    try {
      // Map form chemicals to DTO format
      const chemicalsArray = this.chemicals.controls.map(control => {
        const chemGroup = control as FormGroup;
        const source = chemGroup.get('source')?.value;
        const chemicalId = chemGroup.get('chemicalId')?.value;
        const { unit, perUnit } = parseRateUnit(chemGroup.get('rateUnit')?.value);       // 'gal', 'ac'
        
        // Determine if this is an EPA/ADN chemical or custom chemical
        const result: any = {
          name: chemGroup.get('name')?.value,
          rate: buildRateString(chemGroup.get('rateValue')?.value, chemGroup.get('rateUnit')?.value),
          rateValue: chemGroup.get('rateValue')?.value,
          rateUnit: unit,
          ratePerUnit: perUnit,
          purpose: chemGroup.get('purpose')?.value || undefined
        };

        // Set chemicalId or customChemicalId based on source
        if (source === 'custom' && chemicalId) {
          result.customChemicalId = chemicalId;
        } else if ((source === 'epa' || source === 'adn-recommended') && chemicalId) {
          result.chemicalId = chemicalId;
        }

        return result;
      });

      console.log('Submitting template with chemicals:', chemicalsArray);

      let result;
      if (this.isEditMode && this.templateId) {
        const updateDto: UpdateChemicalTemplateDto = {
          name: this.form.value.name,
          description: this.form.value.description || undefined,
          targetCrops: this.selectedCrops.length > 0 ? this.selectedCrops : undefined,
          targetPests: this.selectedPests.length > 0 ? this.selectedPests : undefined,
          applicationTiming: this.form.value.applicationTiming || undefined,
          totalVolume: this.getFullVolume(),
          estimatedCostPerAcre: this.form.value.estimatedCostPerAcre || undefined,
          notes: this.form.value.notes || undefined,
          chemicals: chemicalsArray
        };
        result = await this.chemicalTemplateService.updateTemplate(this.templateId, updateDto);
      } else {
        const createDto: CreateChemicalTemplateDto = {
          name: this.form.value.name,
          description: this.form.value.description || undefined,
          targetCrops: this.selectedCrops.length > 0 ? this.selectedCrops : undefined,
          targetPests: this.selectedPests.length > 0 ? this.selectedPests : undefined,
          applicationTiming: this.form.value.applicationTiming || undefined,
          totalVolume: this.getFullVolume(),
          estimatedCostPerAcre: this.form.value.estimatedCostPerAcre || undefined,
          notes: this.form.value.notes || undefined,
          chemicals: chemicalsArray
        };
        result = await this.chemicalTemplateService.createTemplate(createDto);
      }

      if (result) {
        console.log('Template saved successfully');
        this.router.navigate(['/logbook/chemicals/templates']);
      } else {
        console.error('Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/logbook/chemicals/templates']);
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }

  isChemicalFieldInvalid(index: number, fieldName: string): boolean {
    const chemical = this.chemicals.at(index) as FormGroup;
    const field = chemical?.get(fieldName);
    return field ? field.invalid && field.touched : false;
  }
}
